import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatTimeInTz, formatDateInTz, formatDateTimeInTz, getActiveTimezone, parseUtcDate } from '../../lib/dateUtils';
import {
  History,
  Download,
  Search,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Monitor,
  User,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  FileText,
  ShieldAlert,
  Flame,
  Filter,
  X,
  Building,
  Globe,
} from 'lucide-react';
import { IosSegmentedControl, SegmentOption } from '../../components/ios/IosSegmentedControl';
import { IosActivityRing } from '../../components/ios/IosActivityRing';
import { ReportModal } from '../../components/ReportModal';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

interface InactivityEventRecord {
  id: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
  thresholdSeconds: number;
  status: 'ACTIVE' | 'RESOLVED' | string;
  user: {
    id: string;
    fullName: string;
    email: string;
    department: string | null;
  };
  device: {
    id: string;
    deviceId: string;
    hostname: string;
    deviceName: string;
  };
}

type RangeOption = 'today' | 'yesterday' | '7d' | '30d' | 'all';
type StatusOption = 'ALL' | 'ACTIVE' | 'RESOLVED';
type SeverityOption = 'ALL' | 'CRITICAL' | 'MODERATE' | 'MINOR';

export function ActivityHistoryPage() {
  const { user } = useAuth();
  const orgTimezone = getActiveTimezone(user?.organization?.timezone);
  const [events, setEvents] = useState<InactivityEventRecord[]>([]);
  const [summary, setSummary] = useState({
    totalEvents: 0,
    totalInactiveSeconds: 0,
    longestInactiveSeconds: 0,
  });
  const [range, setRange] = useState<RangeOption>('today');
  const [statusFilter, setStatusFilter] = useState<StatusOption>('ALL');
  const [severityFilter, setSeverityFilter] = useState<SeverityOption>('ALL');
  const [departmentFilter, setDepartmentFilter] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<InactivityEventRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { range: range === 'all' ? undefined : range };
      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }
      const res = await api.admin.getActivity(params);
      setEvents(res.events || []);
      setSummary(res.summary || { totalEvents: 0, totalInactiveSeconds: 0, longestInactiveSeconds: 0 });
    } catch (err: any) {
      setError(err.message || 'Failed to load inactivity history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [range, statusFilter]);

  const handleExport = async () => {
    try {
      setExporting(true);
      haptics.impact();
      sounds.playTapSound();
      await api.admin.exportActivity({ range });
      sounds.playChimeSound();
    } catch (err: any) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatSeconds = (sec: number) => {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const seconds = sec % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const getSeverity = (durationSeconds: number): { label: string; color: string; badge: string } => {
    if (durationSeconds >= 900) {
      return {
        label: 'Critical (>15m)',
        color: 'text-rose-500 bg-rose-500/10 border-rose-500/30',
        badge: 'bg-rose-500',
      };
    }
    if (durationSeconds >= 300) {
      return {
        label: 'Moderate (5-15m)',
        color: 'text-amber-500 bg-amber-500/10 border-amber-500/30',
        badge: 'bg-amber-500',
      };
    }
    return {
      label: 'Minor (<5m)',
      color: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
      badge: 'bg-blue-500',
    };
  };

  // Extract unique departments
  const departments = Array.from(
    new Set(events.map((e) => e.user?.department).filter(Boolean) as string[])
  );

  // Filtered events
  const filteredEvents = events.filter((e) => {
    const s = search.toLowerCase();
    const matchesSearch =
      !search ||
      e.user?.fullName?.toLowerCase().includes(s) ||
      e.user?.email?.toLowerCase().includes(s) ||
      e.device?.hostname?.toLowerCase().includes(s) ||
      e.device?.deviceName?.toLowerCase().includes(s);

    const matchesDept =
      departmentFilter === 'ALL' || e.user?.department === departmentFilter;

    const matchesSeverity =
      severityFilter === 'ALL' ||
      (severityFilter === 'CRITICAL' && e.durationSeconds >= 900) ||
      (severityFilter === 'MODERATE' && e.durationSeconds >= 300 && e.durationSeconds < 900) ||
      (severityFilter === 'MINOR' && e.durationSeconds < 300);

    return matchesSearch && matchesDept && matchesSeverity;
  });

  // Calculate Employee Idle Leaderboard
  const userIdleMap = events.reduce((acc, ev) => {
    const key = ev.user?.id || ev.device?.id || 'unknown';
    const name = ev.user?.fullName || ev.device?.deviceName || 'Employee';
    const dept = ev.user?.department || 'General';
    if (!acc[key]) {
      acc[key] = { name, dept, totalSecs: 0, count: 0 };
    }
    acc[key].totalSecs += ev.durationSeconds || 0;
    acc[key].count += 1;
    return acc;
  }, {} as Record<string, { name: string; dept: string; totalSecs: number; count: number }>);

  const topInactiveUsers = Object.values(userIdleMap)
    .sort((a, b) => b.totalSecs - a.totalSecs)
    .slice(0, 4);

  const rangeOptions: SegmentOption<RangeOption>[] = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' },
    { id: 'all', label: 'All' },
  ];

  const statusOptions: SegmentOption<StatusOption>[] = [
    { id: 'ALL', label: 'All' },
    { id: 'RESOLVED', label: 'Resolved' },
    { id: 'ACTIVE', label: 'Ongoing' },
  ];

  const severityOptions: SegmentOption<SeverityOption>[] = [
    { id: 'ALL', label: 'All Severity' },
    { id: 'CRITICAL', label: 'Critical (>15m)' },
    { id: 'MODERATE', label: 'Moderate (5-15m)' },
    { id: 'MINOR', label: 'Minor (<5m)' },
  ];

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 max-w-7xl mx-auto w-full min-w-0 animate-ios-enter">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white tracking-tight flex items-center gap-2">
              <span>Activity & Inactivity Intelligence</span>
              <Flame className="w-5 h-5 text-amber-500" />
            </h1>
            <div className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-[11px] font-mono font-medium text-blue-600 dark:text-blue-400">
              <Globe className="w-3.5 h-3.5" />
              <span>{orgTimezone}</span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-medium flex items-center gap-2">
            <span>Visual audit suite tracking idle incidents, working timelines, and employee presence patterns</span>
            <span className="sm:hidden inline-flex items-center gap-1 font-mono text-[10px] text-blue-500">
              • {orgTimezone}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              haptics.selection();
              setIsReportModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-xs font-bold text-white shadow-md shadow-blue-500/20 ios-press transition"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Executive Dossier (PDF)</span>
          </button>

          <button
            onClick={() => {
              haptics.tap();
              sounds.playTapSound();
              fetchEvents();
            }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/80 dark:bg-zinc-900/80 hover:bg-white dark:hover:bg-zinc-800 text-xs font-semibold text-zinc-800 dark:text-zinc-200 border border-black/[0.08] dark:border-white/10 ios-press shadow-sm transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            onClick={handleExport}
            disabled={exporting || events.length === 0}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-pulse-blue hover:bg-blue-600 text-xs font-bold text-white shadow-md shadow-pulse-blue/20 ios-press transition disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            <span>{exporting ? 'Exporting...' : 'CSV'}</span>
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-4 rounded-2xl ios-card">
          <div className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Inactivity Incidents</div>
          <div className="text-2xl font-extrabold text-zinc-900 dark:text-white mt-1 tracking-tight">{summary.totalEvents}</div>
          <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1 font-medium">
            <History className="w-3 h-3 text-blue-500" />
            <span>Events in {range.toUpperCase()} period</span>
          </div>
        </div>

        <div className="p-4 rounded-2xl ios-card bg-amber-500/[0.07] dark:bg-amber-950/20 border-amber-500/30">
          <div className="text-[11px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Cumulative Inactive Time</div>
          <div className="text-2xl font-extrabold text-amber-600 dark:text-amber-300 mt-1 tracking-tight">{formatSeconds(summary.totalInactiveSeconds)}</div>
          <div className="text-[11px] text-amber-600/80 dark:text-amber-400/80 mt-1 flex items-center gap-1 font-medium">
            <Clock className="w-3 h-3 text-amber-500" />
            <span>Total away time across fleet</span>
          </div>
        </div>

        <div className="p-4 rounded-2xl ios-card bg-rose-500/[0.06] dark:bg-rose-950/20 border-rose-500/20">
          <div className="text-[11px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider">Peak Continuous Idle</div>
          <div className="text-2xl font-extrabold text-rose-600 dark:text-rose-300 mt-1 tracking-tight">{formatSeconds(summary.longestInactiveSeconds)}</div>
          <div className="text-[11px] text-rose-600/80 dark:text-rose-400/80 mt-1 flex items-center gap-1 font-medium">
            <TrendingUp className="w-3 h-3 text-rose-500" />
            <span>Longest single recorded incident</span>
          </div>
        </div>
      </div>

      {/* Reimagined: 24-Hour Daytime Timeline Scrubber Visualizer */}
      <div className="p-5 rounded-2xl ios-card space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-500" />
            <h3 className="text-xs font-bold text-zinc-900 dark:text-white uppercase tracking-wider">
              24-Hour Daytime Inactivity Distribution
            </h3>
          </div>
          <div className="flex items-center gap-3 text-[10px] font-medium text-zinc-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
              <span>Active Work</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />
              <span>Minor (&lt;5m)</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />
              <span>Moderate (5-15m)</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />
              <span>Critical (&gt;15m)</span>
            </span>
          </div>
        </div>

        {/* 24-Hour Bar */}
        <div className="relative h-10 w-full bg-zinc-100 dark:bg-zinc-900 rounded-xl overflow-hidden border border-black/5 dark:border-white/5 flex items-center">
          {events.length === 0 ? (
            <div className="w-full text-center text-xs text-zinc-400 font-mono">No incidents recorded in this window</div>
          ) : (
            events.slice(0, 40).map((ev) => {
              const start = parseUtcDate(ev.startedAt);
              let h = 0;
              let m = 0;
              try {
                const parts = new Intl.DateTimeFormat('en-US', {
                  timeZone: orgTimezone,
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: false,
                }).formatToParts(start);
                h = Number(parts.find((p) => p.type === 'hour')?.value) || 0;
                m = Number(parts.find((p) => p.type === 'minute')?.value) || 0;
              } catch {
                h = start.getHours();
                m = start.getMinutes();
              }
              const hour = (h % 24) + m / 60;
              const leftPercent = Math.min(100, Math.max(0, (hour / 24) * 100));
              const widthPercent = Math.min(10, Math.max(1, (ev.durationSeconds / 86400) * 100 * 4));
              const sev = getSeverity(ev.durationSeconds);

              return (
                <div
                  key={ev.id}
                  onClick={() => setSelectedEvent(ev)}
                  title={`${ev.user?.fullName || 'User'} • ${formatSeconds(ev.durationSeconds)} (${formatTimeInTz(start, orgTimezone)})`}
                  style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                  className={`absolute h-7 rounded cursor-pointer transition-all hover:scale-125 hover:z-20 opacity-90 shadow-sm ${sev.badge}`}
                />
              );
            })
          )}
        </div>

        {/* Hour Axis Markers */}
        <div className="flex justify-between text-[10px] font-mono text-zinc-400 px-1">
          <span>00:00</span>
          <span>04:00</span>
          <span>08:00 (Shift Start)</span>
          <span>12:00 (Lunch)</span>
          <span>16:00</span>
          <span>20:00</span>
          <span>23:59</span>
        </div>
      </div>

      {/* Top Inactivity Leaderboard Widget */}
      {topInactiveUsers.length > 0 && (
        <div className="p-4 rounded-2xl bg-zinc-900/40 border border-zinc-800 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4 text-amber-500" />
              <span>Highest Away Time This Period</span>
            </h4>
            <span className="text-[10px] text-zinc-500 font-mono">Automated Inactivity Rollup</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {topInactiveUsers.map((item, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl bg-white/80 dark:bg-zinc-900/90 border border-black/5 dark:border-white/5 flex items-center justify-between"
              >
                <div>
                  <div className="text-xs font-bold text-zinc-900 dark:text-white truncate max-w-[130px]">
                    {item.name}
                  </div>
                  <div className="text-[10px] text-zinc-500">{item.dept} • {item.count} alerts</div>
                </div>
                <span className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-lg border border-amber-500/20">
                  {formatSeconds(item.totalSecs)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Multi-Dimensional Filters and Search */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          <div className="relative w-full lg:w-72">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3.5 top-2.5" />
            <input
              type="text"
              placeholder="Search user, host, department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3.5 py-2 text-xs bg-white dark:bg-zinc-950/70 border border-black/[0.08] dark:border-white/[0.08] rounded-xl text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-pulse-blue backdrop-blur-xl shadow-inner transition-colors"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {departments.length > 0 && (
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-zinc-100 dark:bg-zinc-900 border border-black/5 dark:border-white/5 text-xs">
                <Building className="w-3.5 h-3.5 text-zinc-400 ml-1.5" />
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="bg-transparent text-xs font-semibold text-zinc-800 dark:text-zinc-200 pr-2 py-1 focus:outline-none"
                >
                  <option value="ALL">All Departments</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <IosSegmentedControl
              options={rangeOptions}
              value={range}
              onChange={(r) => setRange(r)}
              className="w-full sm:w-auto"
            />
          </div>
        </div>

        {/* Severity and Status Sub-filter */}
        <div className="flex flex-wrap items-center gap-2">
          <IosSegmentedControl
            options={severityOptions}
            value={severityFilter}
            onChange={(s) => setSeverityFilter(s)}
            className="w-full sm:w-auto"
          />

          <IosSegmentedControl
            options={statusOptions}
            value={statusFilter}
            onChange={(s) => setStatusFilter(s)}
            className="w-full sm:w-auto"
          />
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-3.5 bg-rose-500/10 border border-rose-500/25 rounded-2xl text-xs text-rose-500 dark:text-rose-400 animate-ios-enter">
          {error}
        </div>
      )}

      {/* Reimagined Events Table */}
      <div className="rounded-2xl ios-card overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
            <thead className="bg-black/[0.03] dark:bg-zinc-900/90 text-zinc-500 dark:text-zinc-400 border-b border-black/[0.06] dark:border-white/[0.08] font-bold">
              <tr>
                <th className="px-4 py-3.5">User & Department</th>
                <th className="px-4 py-3.5">Workstation</th>
                <th className="px-4 py-3.5">Incident Started</th>
                <th className="px-4 py-3.5">Resumed At</th>
                <th className="px-4 py-3.5">Idle Duration</th>
                <th className="px-4 py-3.5">Severity Level</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.04] dark:divide-white/[0.05]">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center text-zinc-500 font-medium">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-pulse-blue border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs">Streaming activity audit telemetry...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center text-zinc-500 font-medium">
                    No inactivity events match your filters.
                  </td>
                </tr>
              ) : (
                filteredEvents.map((ev) => {
                  const isOngoing = ev.status === 'ACTIVE';
                  const sev = getSeverity(ev.durationSeconds);

                  return (
                    <tr
                      key={ev.id}
                      onClick={() => setSelectedEvent(ev)}
                      className="hover:bg-black/[0.02] dark:hover:bg-zinc-800/40 transition-colors cursor-pointer"
                    >
                      {/* User */}
                      <td className="px-4 py-3.5">
                        <div className="font-bold text-zinc-900 dark:text-white">{ev.user?.fullName ?? 'Unknown'}</div>
                        <div className="text-[11px] text-zinc-500">{ev.user?.email}</div>
                        {ev.user?.department && (
                          <div className="text-[10px] text-pulse-blue font-bold mt-0.5">{ev.user.department}</div>
                        )}
                      </td>

                      {/* Workstation */}
                      <td className="px-4 py-3.5">
                        <div className="font-semibold text-zinc-800 dark:text-zinc-200">{ev.device?.hostname ?? 'N/A'}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">{ev.device?.deviceName || ev.device?.deviceId}</div>
                      </td>

                      {/* Started */}
                      <td className="px-4 py-3.5 font-mono text-zinc-700 dark:text-zinc-300">
                        <div>{formatTimeInTz(ev.startedAt, orgTimezone)}</div>
                        <div className="text-[10px] text-zinc-400">{formatDateInTz(ev.startedAt, orgTimezone)}</div>
                      </td>

                      {/* Ended */}
                      <td className="px-4 py-3.5 font-mono text-zinc-500">
                        {ev.endedAt ? (
                          <>
                            <div>{formatTimeInTz(ev.endedAt, orgTimezone)}</div>
                            <div className="text-[10px] text-zinc-400">{formatDateInTz(ev.endedAt, orgTimezone)}</div>
                          </>
                        ) : (
                          <span className="text-amber-500 font-semibold italic">Ongoing...</span>
                        )}
                      </td>

                      {/* Duration with Ring */}
                      <td className="px-4 py-3.5 font-mono font-bold text-zinc-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          <IosActivityRing elapsedSeconds={ev.durationSeconds} thresholdSeconds={ev.thresholdSeconds || 120} size={24} />
                          <span>{formatSeconds(ev.durationSeconds)}</span>
                        </div>
                      </td>

                      {/* Severity */}
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${sev.color}`}>
                          {sev.label}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3.5">
                        {isOngoing ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 island-pulse-alert">
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                            Ongoing
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                            Resolved
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3.5 text-right">
                        <span className="inline-flex items-center gap-1 text-pulse-blue font-bold text-xs">
                          <span>Inspect</span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-Over / Modal Incident Inspector Drawer (Rendered via Portal to document.body to guarantee viewport centering) */}
      {selectedEvent &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedEvent(null)}
          >
            <div
              className="bg-white dark:bg-zinc-900 rounded-3xl border border-black/10 dark:border-white/10 max-w-lg w-full p-6 space-y-4 shadow-2xl relative my-auto animate-ios-enter text-left"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-black/5 dark:border-white/5 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-500 font-bold">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-zinc-900 dark:text-white">Inactivity Incident Dossier</h3>
                    <p className="text-xs text-zinc-400">Detailed forensic record of detected idle session</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="p-1.5 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50">
                  <span className="text-zinc-400 block text-[11px]">Employee</span>
                  <span className="font-bold text-zinc-800 dark:text-zinc-200 text-sm">
                    {selectedEvent.user?.fullName || 'Unknown'}
                  </span>
                  <span className="text-[11px] text-zinc-500 block">{selectedEvent.user?.email}</span>
                  <span className="text-[10px] text-pulse-blue font-bold">{selectedEvent.user?.department || 'General'}</span>
                </div>

                <div className="p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50">
                  <span className="text-zinc-400 block text-[11px]">Workstation</span>
                  <span className="font-bold text-zinc-800 dark:text-zinc-200 text-sm">
                    {selectedEvent.device?.deviceName || selectedEvent.device?.hostname}
                  </span>
                  <span className="text-[11px] font-mono text-zinc-500 block truncate">{selectedEvent.device?.deviceId}</span>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                <div>
                  <span className="text-xs text-amber-700 dark:text-amber-300 font-bold block">Total Inactive Duration</span>
                  <span className="text-2xl font-mono font-black text-amber-800 dark:text-amber-200">
                    {formatSeconds(selectedEvent.durationSeconds)}
                  </span>
                </div>
                <IosActivityRing elapsedSeconds={selectedEvent.durationSeconds} thresholdSeconds={selectedEvent.thresholdSeconds || 120} size={42} />
              </div>

              <div className="space-y-2 text-xs text-zinc-600 dark:text-zinc-400 font-mono">
                <div className="flex justify-between py-1 border-b border-black/5 dark:border-white/5">
                  <span>Inactivity Triggered:</span>
                  <span className="text-zinc-900 dark:text-zinc-200">{formatDateTimeInTz(selectedEvent.startedAt, orgTimezone)}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-black/5 dark:border-white/5">
                  <span>Activity Resumed:</span>
                  <span className="text-zinc-900 dark:text-zinc-200">
                    {selectedEvent.endedAt ? formatDateTimeInTz(selectedEvent.endedAt, orgTimezone) : 'Still Ongoing'}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-black/5 dark:border-white/5">
                  <span>Configured Idle Threshold:</span>
                  <span className="text-zinc-900 dark:text-zinc-200">{selectedEvent.thresholdSeconds || 180}s</span>
                </div>
                <div className="flex justify-between py-1 border-b border-black/5 dark:border-white/5">
                  <span>Active Display Timezone:</span>
                  <span className="text-blue-500 font-bold">{orgTimezone}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>Severity Classification:</span>
                  <span className="font-bold text-amber-500">{getSeverity(selectedEvent.durationSeconds).label}</span>
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <Link
                  to={`/admin/live?device=${selectedEvent.device?.deviceId}`}
                  onClick={() => setSelectedEvent(null)}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-1.5 transition shadow-sm"
                >
                  <Monitor className="w-3.5 h-3.5" />
                  <span>Locate Workstation on Live Monitor</span>
                </Link>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* PDF Executive Dossier Modal */}
      <ReportModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} />
    </div>
  );
}
